/**
 * The first-sign-in tour, without a look of its own: which step, where its target is on screen,
 * and the keys. Each app draws the card and the highlight in its own components.
 *
 * A step points at an element marked `data-tour="<target>"`. Steps whose element is not on the
 * page are dropped before the tour starts — that is how the tour follows permissions without a
 * second list of them: a section a person cannot open is never rendered, so it is never a step.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

export type TourStep = {
  /** Matches `data-tour` on the element this step points at. */
  readonly target: string;
  readonly title: string;
  readonly body: string;
  /** Run before the step is shown — to open a drawer, say. The target is looked for afterwards. */
  readonly before?: () => void;
};

export type TourRect = { top: number; left: number; width: number; height: number };

export type TourState = {
  readonly open: boolean;
  readonly step: TourStep | null;
  /** 0-based position among the steps that exist on this page. */
  readonly index: number;
  readonly count: number;
  /** The target's box in viewport pixels, or null while it is being found. */
  readonly rect: TourRect | null;
  readonly next: () => void;
  readonly previous: () => void;
  /** Close: finished, skipped or escaped — all the same to the caller. */
  readonly close: () => void;
};

const find = (target: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-tour="${CSS.escape(target)}"]`);

/** Present and drawn — a collapsed or display:none element has no box to point at. */
const visible = (element: HTMLElement | null): element is HTMLElement => {
  if (element === null) return false;
  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
};

export function useTour({
  steps,
  open,
  onClose,
}: {
  steps: readonly TourStep[];
  open: boolean;
  onClose: () => void;
}): TourState {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<TourRect | null>(null);
  const [present, setPresent] = useState<readonly TourStep[]>([]);
  // False until the page has been looked at for this opening, so "nothing to show" is only ever
  // concluded after looking — not on the first render, before anything was checked.
  const [ready, setReady] = useState(false);

  // Decided once, when the tour opens: which steps have something to point at. A step that needs
  // a `before` (a drawer to open) is kept and checked again when it is reached.
  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    setPresent(steps.filter((step) => step.before !== undefined || visible(find(step.target))));
    setIndex(0);
    setReady(true);
  }, [open, steps]);

  const step = open && ready ? (present[index] ?? null) : null;

  const measure = useCallback(() => {
    if (step === null) return;
    const element = find(step.target);
    if (!visible(element)) {
      setRect(null);
      return;
    }
    const box = element.getBoundingClientRect();
    setRect({ top: box.top, left: box.left, width: box.width, height: box.height });
  }, [step]);

  useLayoutEffect(() => {
    if (step === null) return;
    step.before?.();
    setRect(null);

    // After `before` has had a frame to open whatever it opens.
    const frame = requestAnimationFrame(() => {
      const element = find(step.target);
      if (!visible(element)) {
        // Nothing to point at after all: move on rather than show a card aimed at nothing.
        setPresent((current) => current.filter((candidate) => candidate !== step));
        return;
      }
      element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      measure();
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [step, measure]);

  useEffect(() => {
    if (step === null) return;
    // A drawer sliding in or a page settling moves the target after it was first measured.
    const settle = window.setTimeout(measure, 320);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearTimeout(settle);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step, measure]);

  const count = present.length;

  const next = useCallback(() => {
    if (index >= count - 1) onClose();
    else setIndex(index + 1);
  }, [index, count, onClose]);

  const previous = useCallback(() => {
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight') next();
      else if (event.key === 'ArrowLeft') previous();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, next, previous, onClose]);

  // A tour with nothing to show — or whose last remaining target vanished — closes itself rather
  // than hanging open with no card.
  useEffect(() => {
    if (open && ready && present.length === 0) onClose();
  }, [open, ready, present.length, onClose]);

  // A step dropped from the end leaves the index past the last one.
  useEffect(() => {
    if (ready && present.length > 0 && index > present.length - 1) setIndex(present.length - 1);
  }, [ready, present.length, index]);

  return useMemo(
    () => ({ open: open && step !== null, step, index, count, rect, next, previous, close: onClose }),
    [open, step, index, count, rect, next, previous, onClose],
  );
}
